import { useContext, useEffect, useMemo, useState } from 'react'
import { useOurStore } from '@/app/_providers/StoreProvider'
import StepContext from '@/app/_providers/StepProvider/StepContext'

type UseEditingStateArgs = {
  totalTickets?: number
}

type UseEditingState = {
  isEditing: boolean
  cartUpdating: boolean
  setCartUpdating: (cartUpdating: boolean) => void
}

/**
 * Custom hook to manage the editing state of a ticketing step.
 * It tracks whether the user is currently editing tickets,
 * and whether the cart is being updated after changes are submitted.
 * It also updates the dirty state of the step based on local line items and items to delete.
 * @param {UseEditingStateArgs} args - The arguments for the hook.
 * @param {number} [args.totalTickets=0] - The total number of tickets, defaults to 0.
 * @return {UseEditingState} The editing state and functions to update it.
 */
const useEditingState = ({ totalTickets = 0 } : UseEditingStateArgs): UseEditingState => {
  const {
    localLineItems,
    lineItemsToDelete,
  } = useOurStore((state) => state)
  const { setIsDirty } = useContext(StepContext)
  // cart is being updated after the user submits changes
  const [cartUpdating, setCartUpdating] = useState(false)
  const isEditing = useMemo(() => totalTickets > 0, [totalTickets])

  useEffect(() => {
    setIsDirty(localLineItems.length > 0 || lineItemsToDelete.length > 0)
  }, [localLineItems, lineItemsToDelete, setIsDirty])

  return {
    isEditing,
    cartUpdating,
    setCartUpdating,
  }
}

export default useEditingState
